// The envelope (roadmap 29): at the end of a night shift the screen dims, an envelope slides under the door, ranks 10 to 1 appear
// one at a time, the player's row lights up and the share counts up from 0. Every number here arrives in the server's message
// (game/envelope.js); this file only shows it. Reduced motion shows the final state at once; sound goes through sfx, so mute holds.
// Markup is built below (ids env-*, classes env-*); the look is envelope.css. The words are COPY in game/envelope.js.
// #envelope carries data-state dim | slide | open | done and data-motion full | none (reduced motion) | still (a demo frame).
// On the one-bit desktop ui.js also sets data-at door | foot (where the envelope lies, mailSpot below) and data-paper when the
// letter sits beside the door instead of over the envelope.
import { COPY, solText, countUp } from '../game/envelope.js';
import { verifyHref } from '../game/seed.js';
import { lookLine } from '../game/lit-look.js';
import { litIcon } from './lit-icon.js';

// A share in a row of the list, rounded to 4 places ("0.3176"); a share under 0.00005 SOL reads "<0.0001", none reads "".
// The header says "SOL (rounded)"; the exact share is the one counted up on the slab (solText).
export function rowSol(lamports) {
  const l = BigInt(lamports);
  if (l <= 0n) return '';
  const t = (l + 50_000n) / 100_000n; // ten-thousandths of a SOL, rounded half up
  return t === 0n ? '<0.0001' : `${t / 10_000n}.${String(t % 10_000n).padStart(4, '0')}`;
}

// Where the envelope lies on the one-bit desktop (sprint 17 fix pass): under the door's centre, its body just out from
// under the threshold. Logical pixels of the 640x360 layout, from client/scene-onebit.js: the door leaf runs x 46 to 274
// (centre 160) and the floor starts at row 336. The drawing is 64x64 art pixels (studio/tools/envelope-art.js), its body
// the lower 40 rows, shown at the stage's own scale so its pixels are the room's pixels.
export const SILL = { x: 160, y: 336, doorRight: 274 };
export const MAIL = { size: 64, body: 24 }; // body: the row the envelope's body starts on (above it: the open flap and letter)
// stage: the layout's top-left on screen (px); scale: the stage's integer scale; view: the window; bottom: px of the window
// covered at the bottom (the disclaimer strip); paperW: the letter's width.
// at: 'door' when the floor under the threshold has room for the body (it slides out from under the door), else 'foot'
// (a short window: it lies on the window's bottom edge under the door's centre and slides up into view).
// paperLeft: where the letter starts so it sits beside the door, clear of the envelope; null when there is no room (the
// letter is then centred and the envelope hides once it is open, as on a phone).
export function mailSpot({ stage, scale, view, bottom = 0, paperW = 540, gap = 24, margin = 12 }) {
  const px = scale, x = Math.round(stage.left + SILL.x * px), sill = Math.round(stage.top + SILL.y * px);
  const floorBottom = view.h - bottom, bodyH = (MAIL.size - MAIL.body) * px;
  const at = sill + bodyH + 4 <= floorBottom ? 'door' : 'foot';
  const top = at === 'door' ? sill - MAIL.body * px : floorBottom - 8 - MAIL.size * px;
  const left = Math.round(stage.left + SILL.doorRight * px) + gap;
  return { at, x, top, px, paperLeft: left + paperW + margin <= view.w ? left : null };
}

// slide: from the start of the slide to the first rank; open: when the envelope has landed and the letter opens out of it
export const TIMING = { slide: 900, open: 650, rank: 700, count: 1600, ticks: 32, hold: 400 };

// The order things happen in: [{ at, kind, ...}], times in ms from the message. A pure plan, so it can be tested.
// dim (the room darkens) -> slide (the envelope comes under the door) -> open (the letter zooms out of it) -> ranks 10 to 1 -> finish -> count -> done
export function envelopePlan(msg) {
  const plan = [{ at: 0, kind: 'dim' }, { at: 200, kind: 'slide' }, { at: 200 + TIMING.open, kind: 'open' }];
  let at = TIMING.slide + 200;
  for (const r of msg.ranks) { plan.push({ at, kind: 'rank', rank: r.rank }); at += TIMING.rank; }
  plan.push({ at: at + TIMING.hold, kind: 'finish' });
  const target = BigInt(msg.you.lamports);
  if (target > 0n) for (let i = 1; i <= TIMING.ticks; i++) plan.push({ at: at + TIMING.hold + Math.round(TIMING.count * i / TIMING.ticks), kind: 'count', value: countUp(target, i / TIMING.ticks).toString() });
  plan.push({ at: at + TIMING.hold + (target > 0n ? TIMING.count : 0) + 100, kind: 'done' });
  return plan;
}

// How much of the envelope is still under the door ms into its slide (1 = all, 0 = all out), in the CSS animation's 10 steps
// (env-under, 600 ms, steps(10,end): 4 art rows a step, so every frame lands on whole pixels and nothing is resampled to grey),
// so a still demo frame matches the moving one.
export const slideUnder = ms => 1 - Math.floor(10 * Math.min(1, Math.max(0, ms / 600))) / 10;

const realTimers ={ set: (fn, ms) => setTimeout(fn, ms), clear: h => clearTimeout(h) };

export function createEnvelope({ root, doc = document, timers = realTimers, sfx = () => {} }) {
  let handles = [], refs = null;
  const make = (tag, className, text = '', attrs = {}) => {
    const e = doc.createElement(tag);
    if (className) e.className = className;
    if (text) e.textContent = text;
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
    return e;
  };
  const stop = () => { for (const h of handles) timers.clear(h); handles = []; };

  function build(msg) {
    const rows = new Map(msg.ranks.map(r => {
      const mine = r.rank === msg.you.rank;
      const li = make('li', 'env-row', '', { 'data-rank': String(r.rank), ...(mine ? { 'data-you': 'true' } : {}) });
      li.hidden = true;
      // the player's own share waits (a dotted gap) until the slab has counted up to it, so the row never spoils the count
      const wait = mine && BigInt(r.lamports) > 0n;
      li.append(make('span', 'env-rank', String(r.rank)), make('span', 'env-name', mine ? `${r.name} (${COPY.you})` : r.name),
        make('span', 'env-pts', String(r.points)), make('span', 'env-row-sol', wait ? '' : rowSol(r.lamports), wait ? { 'data-wait': 'true' } : {}));
      return [r.rank, li];
    }));
    // the column heads, so a first-time player can tell the points from the SOL
    const head = make('div', 'env-head');
    head.append(make('span', 'env-head-pts', COPY.points), make('span', 'env-head-sol', COPY.sol));
    const paper = make('div', 'env-paper');
    const list = make('ol', 'env-ranks', '', { 'aria-label': COPY.ranks });
    list.append(...[...rows.values()]);
    const finish = make('p', 'env-finish', msg.you.text, { 'aria-live': 'polite' });
    const sol = make('span', 'env-sol', '0.00');
    const share = make('p', 'env-share'); share.append(make('span', 'env-label', COPY.share + ' '), sol, make('span', 'env-unit', ' SOL'));
    const status = make('p', 'env-status', msg.you.statusText);
    const button = make('button', 'env-close', COPY.close, { type: 'button' });
    finish.hidden = share.hidden = status.hidden = true;
    // the neighbours who knocked in a look this shift (none: no row); data-look is the hook for the one-bit picture
    // each row: the neighbour's head and shoulders in its beam (client/lit-icon.js, 40x40 art pixels at 2x), then the line
    const looks = (msg.looks ?? []).map(l => {
      const p = make('p', 'env-looks', '', { 'data-look': l.look });
      p.append(litIcon(doc, l.look, { w: 40, h: 40, bust: true }), make('span', 'env-looks-line', lookLine(l)));
      return p;
    });
    paper.append(make('h2', 'env-title', COPY.title, { id: 'env-title' }), make('p', 'env-line', COPY.envelope), head, list, ...looks, finish, share, status, make('p', 'env-devnet', COPY.devnet),
      ...(msg.key ? [make('a', 'env-verify', COPY.verify, { href: verifyHref(msg.key), target: '_blank', rel: 'noopener' })] : []), button);
    // the envelope itself: a drawing (envelope.css), so it is hidden from screen readers; the letter is .env-paper
    root.replaceChildren(make('div', 'env-mail', '', { 'aria-hidden': 'true' }), paper);
    root.setAttribute('aria-labelledby', 'env-title');
    root.hidden = false;
    return { paper, rows, finish, share, sol, status, button, hasShare: BigInt(msg.you.lamports) > 0n };
  }

  // The last frame: every rank, the finish line, the exact share. Reduced motion starts here.
  function final(msg) {
    for (const li of refs.rows.values()) li.hidden = false;
    lightYou(msg);
    const own = refs.rows.get(msg.you.rank)?.children[3]; // the share waits until now, when the slab shows it exactly
    if (own?.getAttribute('data-wait') === 'true') { own.textContent = rowSol(msg.you.lamports); own.setAttribute('data-wait', 'false'); }
    refs.finish.hidden = false;
    refs.share.hidden = !refs.hasShare;
    refs.status.hidden = !(refs.hasShare && msg.you.statusText);
    refs.sol.textContent = solText(msg.you.lamports);
    refs.button.textContent = COPY.close;
    root.setAttribute('data-state', 'done');
  }
  const lightYou = msg => refs.rows.get(msg.you.rank)?.setAttribute('data-lit', 'true');

  // at: a still frame that many ms into the reveal (demo screenshots): every step up to then, no timers, no sound, no animation.
  function show(msg, { reduced = false, at } = {}) {
    stop();
    refs = build(msg);
    refs.button.addEventListener('click', () => { if (root.getAttribute('data-state') === 'done') hide(); else { stop(); final(msg); } });
    refs.button.focus?.();
    if (reduced) { root.setAttribute('data-motion', 'none'); final(msg); return; }
    root.setAttribute('data-motion', at === undefined ? 'full' : 'still'); root.setAttribute('data-state', 'dim');
    refs.button.textContent = COPY.skip;
    const plan = envelopePlan(msg);
    if (at !== undefined) {
      for (const step of plan) if (step.at <= at) run(step, msg, true);
      root.style?.setProperty?.('--env-under', String(slideUnder(at - plan.find(s => s.kind === 'slide').at))); // the slide's stepped frame then
      return;
    }
    for (const step of plan) handles.push(timers.set(() => run(step, msg), step.at));
  }
  function run(step, msg, quiet = false) {
    const play = e => { if (!quiet) sfx(e); };
    if (step.kind === 'slide') { root.setAttribute('data-state', 'slide'); play('knock'); }
    else if (step.kind === 'open') { root.setAttribute('data-state', 'open'); refs.button.focus?.(); }
    else if (step.kind === 'rank') { const li = refs.rows.get(step.rank); li.hidden = false; play(step.rank === msg.you.rank ? 'right' : 'tick'); if (step.rank === msg.you.rank) lightYou(msg); }
    else if (step.kind === 'finish') { refs.finish.hidden = false; refs.share.hidden = !refs.hasShare; refs.status.hidden = !(refs.hasShare && msg.you.statusText); }
    else if (step.kind === 'count') refs.sol.textContent = solText(step.value);
    else if (step.kind === 'done') final(msg);
  }
  function hide() { stop(); root.hidden = true; root.replaceChildren(); refs = null; }
  return { show, hide, get open() { return !root.hidden; }, stop };
}
